// @flow

import React, { useCallback, useState, useMemo } from 'react';
import clsx from 'clsx';
import { Message } from '@oneflowab/pomes';
import log from 'logging';
import { sortBy } from 'lodash';

import {
  Select,
  SelectItem,
  SelectIcon,
  SelectContent,
  SelectTrigger,
  SelectValue,
} from 'components/select-compound';
import {
  FormLabel,
} from 'components/form';
import { loadRemainingFonts } from 'font-manager';
import CircularSpinner from 'components/icons/circular-spinner';
import selectStyle from 'components/select-compound/select-compound.module.scss';
import style from './font-select-field.module.scss';

type FontFamily = { label: string, value: string }

type Props = {
  selectedFontFamily: string,
  initialFontFamily: string,
  fontFamilyOptions: FontFamily[],
  setSelectedFontFamily: (FontFamily['value']) => void,
  disabled?: boolean,
}

export const FontSelectField = ({
  selectedFontFamily,
  fontFamilyOptions,
  setSelectedFontFamily,
  disabled = false,
  initialFontFamily,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const alphabeticallySortedOptions = useMemo(() => {
    if (isLoading) {
      return [];
    }
    return sortBy(fontFamilyOptions, 'label');
  }, [fontFamilyOptions, isLoading]);

  const loadAllFontOptions = useCallback(async () => {
    setIsLoading(true);
    try {
      await loadRemainingFonts(initialFontFamily);
    } catch {
      log.error('Typography: Fonts could not be loaded!');
    } finally {
      setIsLoading(false);
    }
  }, [initialFontFamily]);

  const label = (
    <FormLabel className={style.Label} htmlFor="fontFamily">
      <Message
        id="Font"
        comment="Label for field where you select your preferred font"
      />
    </FormLabel>
  );

  const onValueChange = (value: string) => {
    setSelectedFontFamily(value);
  };

  return (
    <div>
      {label}
      <Select
        defaultValue={selectedFontFamily}
        onValueChange={onValueChange}
        disabled={disabled}
        onOpenChange={loadAllFontOptions}
      >
        <SelectTrigger
          id="fontFamily"
          className={clsx(style.SelectTrigger, {
            [style.Loading]: isLoading,
          })}
        >
          <SelectValue />
          {isLoading ? <CircularSpinner className={selectStyle.SelectIcon} /> : <SelectIcon />}
        </SelectTrigger>
        {alphabeticallySortedOptions.length > 0 && (
          <SelectContent>
            {alphabeticallySortedOptions.map((option) => (
              <SelectItem
                key={option.value}
                value={option.value}
                style={{ fontFamily: option.value }}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        )}
      </Select>
    </div>
  );
};
